// src/pages/AlmuerzoMes.js
import React, { useEffect, useMemo, useState } from 'react'
import { api } from './api/casinoApi'
import { useNavigate, useLocation } from 'react-router-dom'
import './casino.css'

function ymNow() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function parseYMD(dateStr) {
  const [y, m, d] = dateStr.split('-').map((x) => Number(x))
  const dt = new Date(y, (m || 1) - 1, d || 1)
  dt.setHours(0, 0, 0, 0)
  return dt
}

function weekdayEs(fechaStr) {
  const dt = parseYMD(fechaStr)
  const fmt = new Intl.DateTimeFormat('es-CL', { weekday: 'long' })
  const s = fmt.format(dt)
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const MENU_FIELDS = [
  { idKey: 'id_fondo1', nameKey: 'fondo1', label: 'FONDO 1' },
  { idKey: 'id_fondo2', nameKey: 'fondo2', label: 'FONDO 2' },
  { idKey: 'id_hipocalorico', nameKey: 'hipocalorico', label: 'HIPOCALÓRICO' },
  { idKey: 'id_vegetariano', nameKey: 'vegetariano', label: 'VEGETARIANO' },
  { idKey: 'id_otro', nameKey: 'otro', label: 'OTRO' },
]

// ✅ valor especial para "No almorzaré" (interno UI)
const NO_LUNCH_ID = 'NO'
const NO_LUNCH_LABEL = 'NO ALMORZARÉ'

// prefijo interno para cuando tenemos guardado un texto que ya no existe en el menú del día
const SAVED_PREFIX = '__SAVED__:' // value del select

const norm = (v) => {
  if (v == null) return ''
  const s = String(v).trim()
  if (!s) return ''
  if (s.toUpperCase() === 'NULL') return ''
  return s
}

const toUpper = (s) => norm(s).toUpperCase()

// ✅ Si viene guardado como "FONDO 2: Pollo", extrae:
// - tag = "FONDO 2"
// - name = "Pollo"
function splitPrefixedText(savedText) {
  const s = norm(savedText)
  if (!s) return { tag: '', name: '' }

  // soporta "TAG: Nombre" o "TAG - Nombre"
  const m = s.match(/^\s*([A-ZÁÉÍÓÚÑ0-9\s]+)\s*[:\-]\s*(.+)\s*$/i)
  if (!m) return { tag: '', name: s }

  return { tag: norm(m[1]), name: norm(m[2]) }
}

// ✅ construye opciones por día: {id, tag, name}
const buildOptions = (row) => {
  const opts = []

  for (const f of MENU_FIELDS) {
    const id = norm(row?.[f.idKey])
    const name = norm(row?.[f.nameKey])
    if (!id || id === '0') continue
    if (!name) continue
    opts.push({ id, tag: f.label, name })
  }

  // quitar duplicados por id
  const seen = new Set()
  const unique = opts.filter((o) => {
    if (seen.has(o.id)) return false
    seen.add(o.id)
    return true
  })

  // agregar NO ALMUERZO
  unique.push({ id: NO_LUNCH_ID, tag: 'OPCIÓN', name: NO_LUNCH_LABEL })
  return unique
}

// ✅ Resuelve lo guardado (texto prefijado o no) a un "value" de select (id o __SAVED__:texto)
function resolveSavedSelectValue(savedVal, options) {
  const v = norm(savedVal)
  if (!v) return ''

  // Si backend guardó NO ALMORZARÉ
  if (toUpper(v) === toUpper(NO_LUNCH_LABEL)) return NO_LUNCH_ID

  // Compatibilidad antigua: si venía un id guardado
  const byId = options.find((o) => o.id === v)
  if (byId) return byId.id

  // Si viene prefijado, intentamos matchear por (tag + name) o solo name
  const { tag, name } = splitPrefixedText(v)

  // Match por (tag, name)
  if (tag && name) {
    const byTagName = options.find(
      (o) => toUpper(o.tag) === toUpper(tag) && toUpper(o.name) === toUpper(name)
    )
    if (byTagName) return byTagName.id
  }

  // Match solo por name (por si cambió el tag o el guardado no trae tag)
  if (name) {
    const byName = options.find((o) => toUpper(o.name) === toUpper(name))
    if (byName) return byName.id
  }

  // Match directo por texto completo contra "TAG: name" (por si guardaste así)
  const byFull = options.find((o) => toUpper(`${o.tag}: ${o.name}`) === toUpper(v))
  if (byFull) return byFull.id

  // No calza con el menú del día => queda como opción "guardada"
  return `${SAVED_PREFIX}${v}`
}

// ✅ Dado un selectValue (id o __SAVED__:texto) devuelve el "almuerzo" (texto) para guardar
// ✅ Ahora guardamos con prefijo "TAG: Nombre"
function selectValueToAlmuerzoText(selectValue, options) {
  const v = norm(selectValue)
  if (!v) return ''

  if (v === NO_LUNCH_ID) return NO_LUNCH_LABEL

  if (v.startsWith(SAVED_PREFIX)) {
    // si ya estaba guardado como texto (posiblemente con prefijo), lo guardamos tal cual
    return norm(v.slice(SAVED_PREFIX.length))
  }

  const opt = options.find((o) => o.id === v)
  if (!opt) return ''

  // ✅ guardado con prefijo
  return `${opt.tag}: ${norm(opt.name)}`
}

export default function AlmuerzosMes() {
  const nav = useNavigate()
  const location = useLocation()

  const [ym, setYm] = useState(ymNow())
  const [menuRows, setMenuRows] = useState([])
  // ✅ Ahora selMap guarda TEXTO del almuerzo: {fecha: "FONDO 2: Pollo al jugo"} o "NO ALMORZARÉ"
  const [selMap, setSelMap] = useState({})
  // ✅ draftMap guarda el value del select (id o NO), hasta que guardas
  const [draftMap, setDraftMap] = useState({})

  const [loading, setLoading] = useState(false)
  const [savingAll, setSavingAll] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })

  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')

  const [missingMark, setMissingMark] = useState({}) // { [fecha]: true }

  const rut = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const rutQuery = (params.get('rut') || '').trim()
    const rutLocal = (localStorage.getItem('rut_almuerzo') || '').trim()
    if (rutQuery) localStorage.setItem('rut_almuerzo', rutQuery)
    return rutQuery || rutLocal
  }, [location.search])

  useEffect(() => {
    if (!rut) nav('/', { replace: true })
  }, [rut, nav])

  // ✅ mapa rápido fecha->row
  const menuByFecha = useMemo(() => {
    const m = {}
    for (const r of menuRows || []) {
      if (r?.fecha) m[r.fecha] = r
    }
    return m
  }, [menuRows])

  const loadAll = async () => {
    if (!rut) return
    setErr('')
    setMsg('')
    setLoading(true)
    setMissingMark({})

    try {
      const [menuRes, selRes] = await Promise.all([
        api.get('/getMenuByMonth.php', { params: { ym } }),
        api.get('/getMySelectionsByMonth.php', { params: { ym, rut } }),
      ])

      if (!menuRes.data?.ok) throw new Error(menuRes.data?.error || 'Error menú')
      if (!selRes.data?.ok) throw new Error(selRes.data?.error || 'Error selecciones')

      const rows = menuRes.data.rows || []
      setMenuRows(rows)

      // ✅ esperamos que selRes devuelva { fecha, almuerzo } (texto)
      // (si por compatibilidad trae almuerzo_id, también lo guardamos tal cual y se resuelve igual)
      const map = {}
      for (const r of selRes.data.rows || []) {
        const saved = norm(r.almuerzo ?? r.almuerzo_texto ?? r.almuerzo_nombre ?? r.almuerzo_id ?? '')
        if (saved) map[r.fecha] = saved
      }
      setSelMap(map)

      setDraftMap({})
    } catch (e) {
      const apiErr = e?.response?.data?.error
      setErr(apiErr || 'No se pudo cargar el mes (menú o selecciones).')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ym, rut])

  // ✅ valor que debe mostrar el select (id, NO o __SAVED__:texto)
  const effectiveSelectValue = (fecha) => {
    const draft = norm(draftMap[fecha] ?? '')
    if (draft) return draft

    const savedVal = selMap[fecha]
    const row = menuByFecha[fecha]
    const options = row ? buildOptions(row) : []
    return resolveSavedSelectValue(savedVal, options)
  }

  const setChoice = (fecha, selectValue) => {
    setDraftMap((prev) => ({ ...prev, [fecha]: String(selectValue) }))
    setMissingMark((prev) => {
      if (!prev[fecha]) return prev
      const copy = { ...prev }
      delete copy[fecha]
      return copy
    })
  }

  const logout = () => {
    localStorage.removeItem('rut_almuerzo')
    nav('/', { replace: true })
  }

  const requiredDates = useMemo(() => {
    return (menuRows || []).filter((r) => Number(r.activo) === 1).map((r) => r.fecha)
  }, [menuRows])

  const missingDates = useMemo(() => {
    const miss = []
    for (const fecha of requiredDates) {
      const chosen = effectiveSelectValue(fecha)
      if (!norm(chosen)) miss.push(fecha)
    }
    return miss
  }, [requiredDates, selMap, draftMap, menuByFecha])

  const scrollToFecha = (fecha) => {
    const el =
      document.getElementById(`day-${fecha}`) ||
      document.getElementById(`row-${fecha}`) ||
      document.getElementById(`card-${fecha}`)

    if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })

    const select = document.getElementById(`select-${fecha}`)
    if (select?.focus) setTimeout(() => select.focus(), 250)
  }

  const saveAll = async () => {
    if (!rut) return
    setErr('')
    setMsg('')

    if (requiredDates.length === 0) {
      setErr('No hay días activos para este mes.')
      return
    }

    if (missingDates.length > 0) {
      const mark = {}
      for (const f of missingDates) mark[f] = true
      setMissingMark(mark)

      const sortedMissing = [...missingDates].sort((a, b) => parseYMD(a) - parseYMD(b))
      const first = sortedMissing[0]

      setErr(`Te faltan ${missingDates.length} día(s). Te llevé al primero pendiente.`)
      scrollToFecha(first)
      return
    }

    setSavingAll(true)

    try {
      // ✅ guardamos solo fechas NO guardadas aún (backend no permite editar)
      const toSave = requiredDates.filter((fecha) => {
        const chosen = effectiveSelectValue(fecha)
        if (!norm(chosen)) return false
        return !selMap[fecha]
      })

      setProgress({ done: 0, total: toSave.length })

      let done = 0
      for (const fecha of toSave) {
        const row = menuByFecha[fecha]
        const options = row ? buildOptions(row) : []

        const selectVal = effectiveSelectValue(fecha) // id o NO
        const almuerzo = selectValueToAlmuerzoText(selectVal, options) // ✅ TEXTO con prefijo

        if (!almuerzo) throw new Error(`No se pudo resolver el almuerzo para ${fecha}`)

        const r = await api.post(
          '/saveMySelection.php',
          { rut, fecha, almuerzo }, // ✅ enviamos TEXTO, no id
          { headers: { 'Content-Type': 'application/json' } }
        )

        if (!r.data?.ok) throw new Error(r.data?.error || `No se pudo guardar ${fecha}`)

        done += 1
        setProgress({ done, total: toSave.length })

        // ✅ marcamos como guardado (texto)
        setSelMap((prev) => ({ ...prev, [fecha]: almuerzo }))
        setDraftMap((prev) => {
          const copy = { ...prev }
          delete copy[fecha]
          return copy
        })
      }

      setMissingMark({})
      setMsg(`✅ Guardado completado. (Se guardaron ${toSave.length} día(s) nuevos).`)
    } catch (e) {
      const apiErr = e?.response?.data?.error
      const status = e?.response?.status

      if (status === 409) {
        setErr(apiErr || 'Hay fechas ya registradas y no se pueden modificar. Recarga para ver tu programación final.')
      } else {
        setErr(apiErr || e?.message || 'Error al guardar todo.')
      }
    } finally {
      setSavingAll(false)
      setProgress({ done: 0, total: 0 })
    }
  }

  const renderBadges = ({ inactive, hasSaved, hasDraft }) => (
    <div className="badges">
      {inactive ? <span className="badge off">No disponible</span> : null}
      {hasSaved ? <span className="badge ok">Guardado</span> : <span className="badge neutral">Sin elegir</span>}
      {hasDraft ? <span className="badge info">Cambios</span> : null}
    </div>
  )

  return (
    <div className="casino-wrap casino-wrap--responsive">
      <div className="casino-card">
        <div className="casino-row casino-row--top">
          <div className="casino-head">
            <h2 className="casino-title">Programación de Almuerzos</h2>
            <div className="casino-sub">
              <div>
                Rut: <b>{rut || '-'}</b>
              </div>
              <div>
                Regla: eliges y guardas una sola vez por día. Puedes seleccionar <b>{NO_LUNCH_LABEL}</b>.
              </div>
            </div>
          </div>

          <div className="casino-controls casino-controls--wrap">
            <input
              className="casino-input"
              type="month"
              value={ym}
              onChange={(e) => setYm(e.target.value)}
              style={{ minWidth: 170 }}
            />

            <button className="casino-btn" onClick={loadAll} disabled={loading || savingAll}>
              {loading ? 'Cargando…' : 'Recargar'}
            </button>

            <button className="casino-btn danger" onClick={logout} disabled={savingAll}>
              Salir
            </button>
          </div>
        </div>

        {menuRows.length > 0 && (
          <div className="casino-alert neutral" style={{ marginTop: 10 }}>
            Días activos: <b>{requiredDates.length}</b> · Pendientes: <b>{missingDates.length}</b> ·
          </div>
        )}

        {msg && <div className="casino-alert ok">{msg}</div>}
        {err && <div className="casino-alert err">{err}</div>}

        {loading ? (
          <div className="casino-alert neutral">Cargando…</div>
        ) : menuRows.length === 0 ? (
          <div className="casino-alert err">No hay menú cargado para este mes.</div>
        ) : (
          <>
            {/* Desktop */}
            <div className="casino-table-wrap hide-on-mobile">
              <table className="casino-table">
                <thead>
                  <tr>
                    <th style={{ width: 170 }}>Día</th>
                    <th>Opciones del día</th>
                    <th style={{ width: 360 }}>Tu elección</th>
                  </tr>
                </thead>
                <tbody>
                  {menuRows.map((row) => {
                    const fecha = row.fecha
                    const inactive = Number(row.activo) !== 1

                    const options = buildOptions(row)

                    const chosenValue = effectiveSelectValue(fecha) // id/NO/__SAVED__
                    const hasSaved = !!selMap[fecha]
                    const hasDraft = draftMap[fecha] != null

                    // si el value es __SAVED__:texto, agregamos opción visual
                    const needsSavedOpt = chosenValue && chosenValue.startsWith(SAVED_PREFIX)
                    const savedText = needsSavedOpt ? chosenValue.slice(SAVED_PREFIX.length) : ''
                    const selectOptions = needsSavedOpt
                      ? [{ id: chosenValue, tag: 'Guardado', name: savedText }, ...options]
                      : options

                    const isMissing = !!missingMark[fecha]

                    return (
                      <tr
                        key={fecha}
                        id={`row-${fecha}`}
                        className={isMissing ? 'row-missing' : ''}
                        style={inactive ? { opacity: 0.65 } : undefined}
                      >
                        <td>
                          <div>
                            <b>{weekdayEs(fecha)}</b>
                          </div>
                          <div>{fecha}</div>
                          {renderBadges({ inactive, hasSaved, hasDraft })}
                          {isMissing ? <div className="missing-text">⚠️ Falta seleccionar</div> : null}
                        </td>

                        <td>
                          {options.length <= 1 ? (
                            <div className="muted">Sin opciones para este día.</div>
                          ) : (
                            <div style={{ display: 'grid', gap: 6 }}>
                              {options
                                .filter((o) => o.id !== NO_LUNCH_ID)
                                .map((o, idx) => (
                                  <div key={o.id}>
                                    {idx + 1}) {o.tag}: {o.name}
                                  </div>
                                ))}
                              <div>
                                <b>5) {NO_LUNCH_LABEL}</b>
                              </div>
                            </div>
                          )}
                        </td>

                        <td id={`day-${fecha}`}>
                          <select
                            id={`select-${fecha}`}
                            className={`casino-select ${isMissing ? 'select-missing' : ''}`}
                            value={chosenValue}
                            disabled={inactive || hasSaved}
                            onChange={(e) => setChoice(fecha, e.target.value)}
                            style={{ width: '100%' }}
                          >
                            <option value="">-- Selecciona --</option>
                            {selectOptions.map((o) => (
                              <option key={`${fecha}-${o.id}`} value={o.id}>
                                {o.id === NO_LUNCH_ID
                                  ? NO_LUNCH_LABEL
                                  : o.id.startsWith?.(SAVED_PREFIX)
                                    ? `Guardado: ${o.name}`
                                    : `${o.tag}: ${o.name}`}
                              </option>
                            ))}
                          </select>

                          {hasSaved ? <div className="muted">Guardado (no se puede modificar).</div> : null}
                          {inactive ? <div className="muted">Menú deshabilitado.</div> : null}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <div className="show-on-mobile">
              <div className="day-cards">
                {menuRows.map((row) => {
                  const fecha = row.fecha
                  const inactive = Number(row.activo) !== 1

                  const options = buildOptions(row)
                  const chosenValue = effectiveSelectValue(fecha) // id/NO/__SAVED__
                  const hasSaved = !!selMap[fecha]
                  const hasDraft = draftMap[fecha] != null

                  const isMissing = !!missingMark[fecha]

                  // para mobile: si está guardado pero no aparece en opciones, lo mostramos como label
                  const needsSavedOpt = chosenValue && chosenValue.startsWith(SAVED_PREFIX)
                  const savedText = needsSavedOpt ? chosenValue.slice(SAVED_PREFIX.length) : ''

                  return (
                    <div
                      key={fecha}
                      id={`card-${fecha}`}
                      className={`day-card ${inactive ? 'is-disabled' : ''} ${isMissing ? 'card-missing' : ''}`}
                    >
                      <div className="day-card__head">
                        <div>
                          <div className="day-card__dow">{weekdayEs(fecha)}</div>
                          <div className="day-card__date">{fecha}</div>
                        </div>
                        {renderBadges({ inactive, hasSaved, hasDraft })}
                      </div>

                      <div className="day-card__body" id={`day-${fecha}`}>
                        {isMissing ? <div className="missing-text">⚠️ Falta seleccionar este día</div> : null}

                        {options.length <= 1 ? (
                          <div className="muted">Sin opciones para este día.</div>
                        ) : (
                          <div className="choice-list">
                            {needsSavedOpt ? (
                              <label className="choice-item disabled">
                                <input type="radio" checked readOnly disabled />
                                <span className="choice-text">
                                  <span className="choice-tag">Guardado</span>
                                  <span className="choice-val">{savedText}</span>
                                </span>
                              </label>
                            ) : null}

                            {options.map((o) => (
                              <label
                                key={`${fecha}-${o.id}`}
                                className={`choice-item ${inactive || hasSaved ? 'disabled' : ''} ${
                                  isMissing && !chosenValue ? 'choice-missing' : ''
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`choice-${fecha}`}
                                  value={o.id}
                                  checked={chosenValue === o.id}
                                  disabled={inactive || hasSaved}
                                  onChange={(e) => setChoice(fecha, e.target.value)}
                                />
                                <span className="choice-text">
                                  {o.id === NO_LUNCH_ID ? (
                                    <b>{NO_LUNCH_LABEL}</b>
                                  ) : (
                                    <>
                                      <span className="choice-tag">{o.tag}</span>
                                      <span className="choice-val">{o.name}</span>
                                    </>
                                  )}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}

                        {hasSaved ? (
                          <div className="muted" style={{ marginTop: 8 }}>
                            Guardado (no se puede modificar).
                          </div>
                        ) : null}
                        {inactive ? (
                          <div className="muted" style={{ marginTop: 8 }}>
                            Menú deshabilitado.
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Guardar todo */}
            <div className="save-footer">
              <div className="save-footer__info">
                <div>
                  <b>Resumen:</b> {requiredDates.length} días activos · {missingDates.length} pendientes
                </div>
                {savingAll ? (
                  <div className="muted">
                    Guardando… <b>{progress.done}/{progress.total}</b>
                  </div>
                ) : missingDates.length > 0 ? (
                  <div className="muted">Si faltan días, te llevaré al primero pendiente.</div>
                ) : (
                  <div className="muted">Todo listo para guardar.</div>
                )}
              </div>

              <button
                className="casino-btn primary"
                onClick={saveAll}
                disabled={loading || savingAll || menuRows.length === 0}
                style={{ minWidth: 170 }}
              >
                {savingAll ? 'Guardando…' : 'Guardar todo'}
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        .casino-row--top { gap: 12px; align-items: flex-start; }
        .casino-controls--wrap { display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end; }
        .casino-head { min-width: 260px; }

        .hide-on-mobile { display: block; }
        .show-on-mobile { display: none; }
        @media (max-width: 920px) {
          .hide-on-mobile { display: none; }
          .show-on-mobile { display: block; }
        }

        .day-cards { display: grid; gap: 12px; margin-top: 10px; }
        .day-card {
          border: 1px solid rgba(0,0,0,.08);
          border-radius: 14px;
          background: #fff;
          box-shadow: 0 8px 22px rgba(0,0,0,.05);
          overflow: hidden;
        }
        .day-card.is-disabled { opacity: .75; }

        .day-card__head {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 12px 10px 12px;
          background: linear-gradient(180deg, rgba(34,197,94,.10), rgba(34,197,94,0));
          border-bottom: 1px solid rgba(0,0,0,.06);
        }
        .day-card__dow { font-weight: 700; color: #166534; }
        .day-card__date { color: #4b5563; font-size: 0.95rem; }

        .day-card__body { padding: 10px 12px 12px 12px; }
        .choice-list { display: grid; gap: 8px; }

        .choice-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 10px 10px;
          border-radius: 12px;
          border: 1px solid rgba(0,0,0,.07);
          background: #f8fafc;
          cursor: pointer;
        }
        .choice-item:hover { background: #f0fdf4; border-color: rgba(34,197,94,.35); }
        .choice-item.disabled { cursor: not-allowed; opacity: .85; }
        .choice-item input { margin-top: 3px; transform: scale(1.1); }

        .choice-text { display: flex; flex-direction: column; gap: 4px; }
        .choice-tag {
          display: inline-block;
          font-size: .75rem;
          font-weight: 700;
          color: #166534;
          background: rgba(34,197,94,.12);
          padding: 2px 8px;
          border-radius: 999px;
          width: fit-content;
        }
        .choice-val { color: #111827; font-weight: 600; line-height: 1.2; }

        .save-footer{
          position: sticky;
          bottom: 0;
          margin-top: 16px;
          padding: 12px;
          border-radius: 14px;
          border: 1px solid rgba(0,0,0,.08);
          background: rgba(255,255,255,0.92);
          backdrop-filter: blur(4px);
          box-shadow: 0 10px 26px rgba(0,0,0,.08);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .save-footer__info{ display: flex; flex-direction: column; gap: 4px; color: #111827; }
        @media (max-width: 520px){
          .save-footer{ flex-direction: column; align-items: stretch; }
        }

        .missing-text{
          margin-top: 6px;
          color: #b91c1c;
          font-weight: 700;
          font-size: 0.9rem;
        }
        .row-missing{
          outline: 2px solid rgba(220,38,38,.55);
          outline-offset: -2px;
          background: rgba(254,226,226,.45);
        }
        .select-missing{
          border: 2px solid rgba(220,38,38,.75) !important;
          background: rgba(254,226,226,.35) !important;
        }
        .card-missing{
          border: 2px solid rgba(220,38,38,.65);
          box-shadow: 0 10px 28px rgba(220,38,38,.18);
        }
        .choice-missing{
          border-color: rgba(220,38,38,.45);
          background: rgba(254,226,226,.35);
        }
      `}</style>
    </div>
  )
}